import type { Mixer, NoiseKind } from './mixer';

/**
 * Small one-shot synthesis toolkit shared by impacts, UI, stingers, ambience events and the radio.
 * Every helper schedules at absolute audio time `t`, stops its sources and disconnects its nodes on end.
 */

export function midiToHz(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}

/** Percussive (no `release`) or sustained (hold until `dur - release`) exponential-decay envelope. */
export function envelope(p: AudioParam, t: number, dur: number, peak: number, attack: number, release?: number): void {
  const a = Math.max(0.001, Math.min(attack, dur * 0.92));
  p.setValueAtTime(0.0001, t);
  p.linearRampToValueAtTime(Math.max(0.0001, peak), t + a);
  if (release !== undefined && release < dur - a) p.setValueAtTime(Math.max(0.0001, peak), t + dur - release);
  p.exponentialRampToValueAtTime(0.0001, t + dur);
}

export interface ToneOpts {
  type?: OscillatorType;
  wave?: PeriodicWave;
  f0: number;
  /** Exponential sweep target reached at `t + sweepTime` (default `dur`). */
  f1?: number;
  sweepTime?: number;
  t: number;
  dur: number;
  gain: number;
  attack?: number;
  release?: number;
  detune?: number;
  lp?: number;
  lpTo?: number;
  bp?: number;
  bpTo?: number;
  q?: number;
  dest: AudioNode;
  /** Radio/essential voices ignore the one-shot voice cap. */
  essential?: boolean;
  /** Pitch vibrato: an LFO into detune, fading in over `delay` seconds (strings, flute, organ). */
  vib?: { hz: number; cents: number; delay?: number };
}

function dropAll(nodes: AudioNode[]): void {
  for (const n of nodes) {
    try {
      n.disconnect();
    } catch {
      /* already disconnected */
    }
  }
}

/** One oscillator -> optional filters -> enveloped gain -> dest. */
export function tone(mx: Mixer, o: ToneOpts): void {
  const ctx = mx.ctx;
  if (!ctx || !mx.canSpawn(o.essential)) return;
  const osc = ctx.createOscillator();
  if (o.wave) osc.setPeriodicWave(o.wave);
  else osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.f0, o.t);
  if (o.f1 !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.f1), o.t + (o.sweepTime ?? o.dur));
  if (o.detune) osc.detune.value = o.detune;
  const nodes: AudioNode[] = [osc];
  let tail: AudioNode = osc;
  if (o.lp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = o.q ?? 0.7;
    f.frequency.setValueAtTime(o.lp, o.t);
    if (o.lpTo !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.lpTo), o.t + o.dur);
    tail.connect(f);
    tail = f;
    nodes.push(f);
  }
  if (o.bp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = o.q ?? 2;
    f.frequency.setValueAtTime(o.bp, o.t);
    if (o.bpTo !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.bpTo), o.t + o.dur);
    tail.connect(f);
    tail = f;
    nodes.push(f);
  }
  const g = ctx.createGain();
  envelope(g.gain, o.t, o.dur, o.gain, o.attack ?? 0.004, o.release);
  tail.connect(g);
  g.connect(o.dest);
  nodes.push(g);
  let lfo: OscillatorNode | null = null;
  if (o.vib && o.vib.cents > 0) {
    lfo = ctx.createOscillator();
    lfo.frequency.value = o.vib.hz;
    const depth = ctx.createGain();
    const d = Math.min(o.vib.delay ?? 0, o.dur * 0.6);
    depth.gain.setValueAtTime(0, o.t);
    depth.gain.linearRampToValueAtTime(o.vib.cents, o.t + d + 0.08);
    lfo.connect(depth).connect(osc.detune);
    nodes.push(lfo, depth);
  }
  mx.voiceCount++;
  osc.onended = () => {
    mx.voiceCount--;
    dropAll(nodes);
  };
  osc.start(o.t);
  osc.stop(o.t + o.dur + 0.03);
  if (lfo) {
    lfo.start(o.t);
    lfo.stop(o.t + o.dur + 0.03);
  }
}

export interface NoiseOpts {
  kind?: NoiseKind;
  t: number;
  dur: number;
  gain: number;
  attack?: number;
  release?: number;
  filter?: BiquadFilterType;
  f0?: number;
  /** Exponential filter sweep target reached at the end of `dur`. */
  f1?: number;
  q?: number;
  dest: AudioNode;
  essential?: boolean;
}

/** Enveloped (optionally filter-swept) noise burst. */
export function noiseBurst(mx: Mixer, o: NoiseOpts): void {
  const ctx = mx.ctx;
  if (!ctx || !mx.canSpawn(o.essential)) return;
  const src = mx.noiseSource(o.kind ?? 'white');
  const nodes: AudioNode[] = [src];
  let tail: AudioNode = src;
  if (o.filter) {
    const f = ctx.createBiquadFilter();
    f.type = o.filter;
    f.Q.value = o.q ?? 1;
    f.frequency.setValueAtTime(o.f0 ?? 1000, o.t);
    if (o.f1 !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), o.t + o.dur);
    tail.connect(f);
    tail = f;
    nodes.push(f);
  }
  const g = ctx.createGain();
  envelope(g.gain, o.t, o.dur, o.gain, o.attack ?? 0.002, o.release);
  tail.connect(g);
  g.connect(o.dest);
  nodes.push(g);
  mx.voiceCount++;
  src.onended = () => {
    mx.voiceCount--;
    dropAll(nodes);
  };
  mx.startNoise(src, o.t);
  src.stop(o.t + o.dur + 0.03);
}

export interface SweepOpts {
  kind?: NoiseKind;
  t: number;
  dur: number;
  gain: number;
  /** Band centre at the start, the peak (at `peakAt` of the duration) and the end. */
  f0: number;
  fPeak: number;
  f1: number;
  peakAt?: number;
  q?: number;
  dest: AudioNode;
}

/**
 * Noise through a bandpass that sweeps up then down with a matching swell: the "whoosh" of something
 * passing (a swung bat, a car going by, a gust).
 */
export function sweepNoise(mx: Mixer, o: SweepOpts): void {
  const ctx = mx.ctx;
  if (!ctx || !mx.canSpawn()) return;
  const src = mx.noiseSource(o.kind ?? 'pink');
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = o.q ?? 1.5;
  const pk = o.t + o.dur * Math.min(0.9, Math.max(0.1, o.peakAt ?? 0.55));
  const end = o.t + o.dur;
  bp.frequency.setValueAtTime(Math.max(20, o.f0), o.t);
  bp.frequency.exponentialRampToValueAtTime(Math.max(20, o.fPeak), pk);
  bp.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), end);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, o.t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, o.gain), pk);
  g.gain.exponentialRampToValueAtTime(0.0001, end);
  src.connect(bp).connect(g).connect(o.dest);
  mx.voiceCount++;
  src.onended = () => {
    mx.voiceCount--;
    dropAll([src, bp, g]);
  };
  mx.startNoise(src, o.t);
  src.stop(end + 0.03);
}

/**
 * A resonant "ring" excited by a click: a short noise tick into a high-Q bandpass (wood tock, glass
 * tink, a revolver's metal, a bell clapper).
 */
export function ringTick(mx: Mixer, dest: AudioNode, t: number, f: number, q: number, gain: number, dur = 0.12, essential = false): void {
  noiseBurst(mx, { kind: 'white', t, dur: Math.max(0.015, dur), gain, attack: 0.0008, filter: 'bandpass', f0: f, q, dest, essential });
}

/** Inharmonic struck-metal partials (bells, car-body ring, coin pings). */
export function bellPartials(
  mx: Mixer,
  dest: AudioNode,
  t: number,
  base: number,
  dur: number,
  gain: number,
  ratios: readonly number[] = [1, 2.76, 5.4, 8.93],
  essential = false,
): void {
  for (let i = 0; i < ratios.length; i++) {
    const r = ratios[i] as number;
    const f = base * r;
    if (f > 16000) continue;
    tone(mx, { type: 'sine', f0: f, t, dur: dur / (1 + i * 0.7), gain: gain / (1 + i * 0.9), attack: 0.002, dest, essential });
  }
}

/** Soft-clip curve for WaveShaperNodes. `k` = drive. Pure, so tests can inspect it. */
export function softClipCurve(k: number, n = 1024): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  const norm = Math.tanh(k);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(k * x) / norm;
  }
  return c;
}
